import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { OryAuthFacade } from '@ory-auth';
import { concatMap, map, tap, withLatestFrom } from 'rxjs';

import { NavSegments } from '@aw/shared/models';
import { LocalStorageService } from '@aw/shared/services/local-storage';

import { redirectBusinessUnitStorageKey } from '@aw/prypco/constants';
import { Module, Page } from '@aw/prypco/enums';
import { BlocksPage } from '@aw/prypco/modules/blocks/enum';
import { BlocksService } from '@aw/prypco/modules/blocks/services/blocks';
import {
  ReferenceDataCatalogue,
  ReferenceDataService,
} from '@aw/prypco/services/reference-data';
import {
  AmplitudeEventName,
  AmplitudeService,
  GtmService,
} from '@aw/prypco/services/script-manager';
import { UserService } from '@aw/prypco/services/user';
import { SplashScreenFacade } from '@aw/prypco/state/splash-screen';

import * as AuthActions from './auth.actions';
import { AuthFacade } from './auth.facade';
import { AuthUtils } from './utils/auth.utils';

@Injectable()
export class AuthEffects {
  protected readonly oryAuthFacade = inject(OryAuthFacade);
  protected readonly referenceDataService = inject(ReferenceDataService);
  protected readonly amplitudeService = inject(AmplitudeService);
  protected readonly store = inject(Store);
  protected readonly authFacade = inject(AuthFacade);
  protected readonly actions$ = inject(Actions);
  protected readonly userService = inject(UserService);
  protected readonly splashScreenFacade = inject(SplashScreenFacade);
  protected readonly router = inject(Router);
  protected readonly localStorageService = inject(LocalStorageService);
  protected readonly blocksService = inject(BlocksService);
  protected readonly gtmService = inject(GtmService);

  init$ = createEffect(() => this.actions$.pipe(ofType(AuthActions.init)));

  logOut$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.logOut),
        tap(({ businessUnit }) => {
          this.localStorageService.set(
            redirectBusinessUnitStorageKey,
            businessUnit,
          );

          // Clear the auth state first, then let Ory handle logout and navigation
          this.oryAuthFacade.logout();
        }),
      ),
    {
      dispatch: false,
    },
  );

  signUp$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.signUp),
        map(({ redirectUri }) => redirectUri),
        withLatestFrom(this.authFacade.authenticated$),
        tap(async ([redirectUri, authenticated]) => {
          if (authenticated) {
            this.router.navigate(redirectUri);
          }

          const trackingParams = this.buildTrackingQueryParams();
          const queryParams = new URLSearchParams();

          Object.entries(trackingParams).forEach(([key, value]) => {
            queryParams.append(key, String(value));
          });
          const redirectToParams = queryParams.toString();

          let redirectPath = redirectUri;
          if (!redirectPath?.length) {
            redirectPath = ['/', BlocksPage.TermsAndConditions];
          }

          const redirectUrl = `${location.protocol}//${
            location.host
          }/${redirectPath.join('/')}?${redirectToParams}`;

          this.oryAuthFacade.setRedirectParams(redirectUrl);
          this.oryAuthFacade.redirectToRegistration();
        }),
      ),
    { dispatch: false },
  );

  signIn$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.signIn),
        map(({ redirectUri }) => redirectUri),
        withLatestFrom(this.authFacade.authenticated$),
        tap(async ([redirectUri, authenticated]) => {
          if (authenticated) {
            this.router.navigate(redirectUri);
          }

          const trackingParams = this.buildTrackingQueryParams();
          const queryParams = new URLSearchParams();

          Object.entries(trackingParams).forEach(([key, value]) => {
            queryParams.append(key, String(value));
          });
          const redirectToParams = queryParams.toString();

          const redirectUrl = `${location.protocol}//${
            location.host
          }/${redirectUri.join('/')}?${redirectToParams}`;

          this.oryAuthFacade.setRedirectParams(redirectUrl);
          this.oryAuthFacade.redirectToLogin();
        }),
      ),
    { dispatch: false },
  );

  refreshAccountStates$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.refreshAccountStates),
        // ORYTODO: possible that refresh user is needed (loadUser)
        concatMap(() => this.authFacade.refreshAccountStates()),
      ),
    { dispatch: false },
  );

  protected buildTrackingQueryParams(): {
    analytics?: string;
    deviceId?: string;
  } {
    if (this.amplitudeService.shouldTrack && this.amplitudeService.deviceId) {
      return {
        analytics: 'true',
        deviceId: this.amplitudeService.deviceId,
      };
    }
    return {};
  }

  protected readonly composeRedirectUrl = (segments: NavSegments): string =>
    encodeURI(
      `${window.location.protocol}//${window.location.host}/${segments.join('/')}`,
    );
}
